import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { CalendarBlank, CaretLeft, CaretRight } from "@phosphor-icons/react";

const badge = (d) => d < 0 ? "bg-zinc-100 text-zinc-500 border-zinc-200" : d <= 7 ? "bg-rose-50 text-rose-700 border-rose-200" : d <= 30 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-zinc-50 text-zinc-600 border-zinc-200";
const label = (d) => d < 0 ? `${-d}d ago` : d === 0 ? "today" : `in ${d}d`;
const KIND_DOT = { engagement: "bg-indigo-500", statutory: "bg-rose-500", estimate: "bg-amber-500" };

export function DeadlineCalendar({ compact = false }) {
  const today = new Date();
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const { data } = useQuery({ queryKey: ["deadline-calendar"], queryFn: () => api.get("/calendar/deadlines").then((r) => r.data) });
  const events = data?.events || [];
  const byDate = events.reduce((m, e) => { (m[e.date] = m[e.date] || []).push(e); return m; }, {});
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const iso = (d) => `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const upcoming = events.filter((e) => e.days_left >= 0).slice(0, compact ? 5 : 8);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="deadline-calendar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><CalendarBlank size={15} /> Filing deadline calendar</h3>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="text-zinc-400 hover:text-zinc-900"><CaretLeft size={14} /></button>
          <span data-testid="cal-month-label" className="text-xs font-mono text-zinc-600 w-24 text-center">{month.toLocaleString("en-US", { month: "long", year: "numeric" })}</span>
          <button data-testid="cal-next" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="text-zinc-400 hover:text-zinc-900"><CaretRight size={14} /></button>
        </div>
      </div>
      <div className={`grid grid-cols-1 ${compact ? "" : "lg:grid-cols-2"} gap-4`}>
        <div>
          <div className="grid grid-cols-7 text-center text-[9px] uppercase text-zinc-400 mb-1">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <span key={d}>{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              const evts = d ? byDate[iso(d)] || [] : [];
              const isToday = d && iso(d) === today.toISOString().slice(0, 10);
              return (
                <div key={i} data-testid={d ? `cal-day-${iso(d)}` : undefined} title={evts.map((e) => e.title).join("\n")}
                  className={`h-9 rounded-sm text-[10px] flex flex-col items-center justify-center ${isToday ? "bg-zinc-900 text-white" : evts.length ? "bg-zinc-50 border border-zinc-200 text-zinc-800" : "text-zinc-400"}`}>
                  {d && <span>{d}</span>}
                  {evts.length > 0 && (
                    <span className="flex gap-0.5 mt-0.5">
                      {evts.slice(0, 3).map((e, j) => <span key={j} className={`w-1 h-1 rounded-full ${KIND_DOT[e.kind]}`} />)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[9px] text-zinc-400 mt-2 flex gap-3">
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" /> your returns</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> statutory</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> estimates</span>
          </p>
        </div>
        <div data-testid="upcoming-deadlines">
          <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Coming up</p>
          <div className="space-y-1.5">
            {upcoming.map((e, i) => (
              <div key={i} data-testid={`deadline-event-${e.date}-${i}`} className="flex items-center justify-between gap-2 text-xs border-b border-zinc-100 last:border-0 py-1.5">
                <span className="text-zinc-700 truncate flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full shrink-0 ${KIND_DOT[e.kind]}`} />{e.title}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <span className="font-mono text-[10px] text-zinc-400">{e.date}</span>
                  <span className={`text-[9px] border rounded-sm px-1.5 py-0.5 font-medium ${badge(e.days_left)}`}>{label(e.days_left)}</span>
                </span>
              </div>
            ))}
            {!upcoming.length && <p className="text-xs text-zinc-400 italic">No upcoming deadlines.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
